import {
  Component,
  h,
  Element,
  Listen,
  Prop,
  State,
  Watch,
  forceUpdate,
} from "@stencil/core"
import {
  animation,
  getAppearance,
  translate,
} from "../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-scroll-to-top",
  styleUrl: "scroll-to-top.scss",
  shadow: true,
})
export class ScrollToTop {
  @Element() public el!: HTMLSdxScrollToTopElement

  private readonly animationDuration = 500
  private readonly animationTranslationY = 112

  /**
   * @private
   */
  @Prop() public position: "fixed" | "static" = "fixed"

  /**
   * @private
   */
  @Prop() public demo: boolean = false

  /**
   * @private
   * Disable animations for testing.
   */
  @Prop() public animated: boolean = true

  @State() private visible: boolean = false

  public componentWillLoad() {
    this.visible = this.isVisible()
    this.updateVisibility()
  }

  @Listen("scroll", { target: "window" })
  public onWindowScroll() {
    this.visible = this.isVisible()
  }

  @Listen("touchstart")
  public onTouchStart() {
    // NOP - make sure touchstart is noticed. touchstart event needed for the animations (note: tracking on prod)
  }

  private isVisible(): boolean {
    // should always be visible when "demo"; only used for demo on SDX documentation site
    return this.demo || this.hasScrolledOverWindowHeight()
  }

  private hasScrolledOverWindowHeight(): boolean {
    // do not use window.innerHeight because of iOS
    return window.pageYOffset > document.documentElement.clientHeight
  }

  @Listen("click")
  public onClick() {
    this.scrollToTop()
  }

  @Watch("visible")
  public onVisibleChanged() {
    this.updateVisibility()
  }

  private showEl() {
    this.el.style.display = "block"

    if (this.position === "fixed") {
      this.el.style.position = "fixed"
      this.el.style.bottom = "32px" // $baseline-4
      this.el.style.right = "32px" // $baseline-4
    }
  }

  private updateVisibility() {
    if (this.visible) {
      this.showEl()

      animation.start(
        this.el,
        {
          opacity: "1",
          transform: "translateY(0)",
        },
        this.animated ? this.animationDuration : 0,
      )
    } else {
      animation.start(
        this.el,
        {
          opacity: "0",
          transform: `translateY(${this.animationTranslationY}px`,
        },
        this.animated ? this.animationDuration : 0,
        {
          finished: () => {
            animation.set(this.el, { display: "none" })
          },
        },
      )
    }

    forceUpdate(this.el)
  }

  private getComponentClassNames() {
    return {
      [getAppearance(this.el)]: true,
    }
  }

  public scrollToTop() {
    window.scroll({ top: 0, behavior: "smooth" })
  }

  public render() {
    return (
      <div class={this.getComponentClassNames()}>
        <button
          type="button"
          class="scroll-to-top"
          aria-label={translate("Scroll to top", this.el.lang)}
        >
          <sdx-icon icon-name="icon-arrow-up" size={3} />
        </button>
      </div>
    )
  }
}
